import * as amplitude from "@amplitude/analytics-browser";
import { styled, TooltipProps } from "@mui/material";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { AI_AUTOPILOT_USER_ID } from "@redotech/ai-autopilot-constants/constant";
import { useRequiredContext } from "@redotech/react-util/context";
import CornerUpLeftDoubleSvg from "@redotech/redo-icons/corner-up-left-double.svg";
import CornerUpLeftSvg from "@redotech/redo-icons/corner-up-left_filled.svg";
import CornerUpRightSvg from "@redotech/redo-icons/corner-up-right_filled.svg";
import DotsIconSvg from "@redotech/redo-icons/dots-horizontal.svg";
import InfoSvg from "@redotech/redo-icons/info-circle.svg";
import PaperclipSvg from "@redotech/redo-icons/paperclip_filled.svg";
import StarsIcon from "@redotech/redo-icons/stars-01.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { EmailMessageContainer } from "@redotech/redo-merchant-app-common/email-message-container";
import * as emailMessageCss from "@redotech/redo-merchant-app-common/email-message.module.css";
import { SimpleEmailMessage } from "@redotech/redo-merchant-app-common/simple-email-message";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  ExpandedConversation,
  ExpandedConversationMessage,
  MessageVisibility,
  MinimalConversation,
} from "@redotech/redo-model/conversation";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import {
  EmailAddressInfo,
  EmailAddressInfoWithValidEmail,
  EmailReplyType,
  filterAndPreprocessEmailInfo,
  hasExactlyOneRecipientEmail,
  stringifyEmailRecipientsInfo,
} from "@redotech/redo-model/support/conversations/email-info";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import {
  RedoButton,
  RedoButtonProps,
} from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { AttachmentThumbnail } from "@redotech/redo-web/attachment-thumbnail";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import classNames from "classnames";
import {
  Dispatch,
  memo,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { AIFeedback } from "../ai-feedback";
import { ConversationSendStateContext } from "../conversation-send-states";
import { getPublicDraftReplyingToMessage } from "../message-input-utils";
import { MessageInput } from "../message-input/message-input";
import { EmailAddressesTooltip } from "./email-address-tooltip";
import { EmailDraftState } from "./email-draft-state";
import {
  convertPlainTextToHtml,
  cutOutRepliesFromMessageContent,
} from "./format-email-message-content";

const CustomTooltip = styled(function CustomTooltip({
  className,
  ...props
}: TooltipProps) {
  return <Tooltip {...props} classes={{ popper: className }} />;
})(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#0F0F0F",
    color: "#FFFFFF",
    fontSize: "var(--redo-body-xsmall-text-size) !important",
    font: "var(--redo-body-font-family)",
    padding: "8px 12px",
    borderRadius: "var(--redo-corner-small-radius)",
    lineHeight: "18px",
    maxWidth: 400,
  },
}));

export type MessageContent =
  | { type: "messageText"; messageText: string }
  | { type: "messageHtml"; messageHtml: string };

export type MessageInputProps = {
  setActiveConversation: (
    conversation: MinimalConversation | undefined,
  ) => void;
  setErrorMessage: Dispatch<SetStateAction<string>>;
  setShowErrorMessage: Dispatch<SetStateAction<boolean>>;
  showFullCommentThread: boolean;
  typing: Record<string, Date>;
  setTyping: Dispatch<SetStateAction<Record<string, Date>>>;
  setShowFullCommentThread: Dispatch<SetStateAction<boolean>>;
  nextConversationInList?: MinimalConversation;
  prevConversationInList?: MinimalConversation;
};

export const EmailMessage = memo(function EmailMessage({
  startCollapsed,
  userAvatarImageUrl,
  messageContent,
  from,
  to,
  cc,
  bcc,
  date,
  subject,
  files,
  indexOfChatConversionMessage,
  visibility,
  isUser,
  message,
  messageIndex,
  conversationJustMerged,
  conversation,
  profilePictures,
  whichDraftModeToStartWith,
  setActiveConversation,
  setErrorMessage,
  setShowErrorMessage,
  showFullCommentThread,
  typing,
  setTyping,
  setShowFullCommentThread,
  nextConversationInList,
  prevConversationInList,
  removeConversationFromProximity,
  id,
}: {
  startCollapsed: boolean;
  userAvatarImageUrl: string;
  messageContent: MessageContent;
  from: EmailAddressInfoWithValidEmail | undefined;
  to: EmailAddressInfoWithValidEmail[];
  cc: EmailAddressInfoWithValidEmail[];
  bcc: EmailAddressInfoWithValidEmail[];
  date: string;
  subject: string;
  files: any[];
  indexOfChatConversionMessage: number | null;
  visibility: MessageVisibility;
  isUser?: boolean;
  message: ExpandedConversationMessage;
  messageIndex: number;
  conversationJustMerged?: boolean;
  conversation: ExpandedConversation;
  profilePictures: Record<string, string> | undefined;
  whichDraftModeToStartWith: EmailReplyType | undefined;
  removeConversationFromProximity: (
    conversationToExclude: MinimalConversation,
  ) => void;
  id?: string;
} & MessageInputProps) {
  const [
    shouldPopupRecipientsModalBecauseForwardButtonClicked,
    setShouldPopupRecipientsModalBecauseForwardButtonClicked,
  ] = useState(false);
  useEffect(() => {
    setShouldPopupRecipientsModalBecauseForwardButtonClicked(false);
  }, [conversation._id]);
  const team = useRequiredContext(TeamContext);

  const canReply = useHasPermission(SupportPermissions.CREATE_REPLY);

  const shouldShowReplyButton = !!to.length;

  const conversationSendStates = useContext(ConversationSendStateContext);

  const shouldShowReplyAllButton =
    !hasExactlyOneRecipientEmail({ to, cc, bcc }) || !to.length;

  /** Don't actually "reply" to your own messages lol. Reply to the original recipients when applicable */
  const shouldReuseThisToRecipientsForNewToRecipients =
    from?.email === conversation.email?.email;

  /**
   * compare emails regardless of aliases (sender addr to the right of the "+" sign infront of "@")
   */
  const compareEmailsRegardlessOfAliases = (
    email1: EmailAddressInfo | undefined,
    email2: EmailAddressInfo | undefined,
  ): boolean => {
    if (!email1?.email || !email2?.email) {
      return false;
    }
    const getEmailWithoutAlias = (email: string): string => {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      return email.split("@")[0].split("+")[0] + "@" + email.split("@")[1];
    };

    try {
      return (
        getEmailWithoutAlias(email1.email) ===
        getEmailWithoutAlias(email2.email)
      );
    } catch (error) {
      console.error("Error comparing emails:", error);
      return false;
    }
  };

  /**
   * Helper function to filter a list of EmailAddressInfo that
   * if the current "from" email is in the CC or BCC list, remove it.
   */
  const filterOutSelfFromCcOrBcc = (
    emailAddresses: EmailAddressInfo[],
  ): EmailAddressInfo[] => {
    return emailAddresses.filter(
      (emailAddress) => !compareEmailsRegardlessOfAliases(emailAddress, from),
    );
  };

  const initialDraftToAssignment = (mode: EmailReplyType) =>
    /** Reply uses only the "to" email addresses */
    mode === "Reply"
      ? shouldReuseThisToRecipientsForNewToRecipients
        ? (to ?? [])
        : from
          ? [from]
          : []
      : /** (when replying all, include new people who were looped in from your reply)*/
        mode === "Reply All"
        ? shouldReuseThisToRecipientsForNewToRecipients
          ? (to ?? [])
          : [
              ...(from ? [from] : []),
              ...to.filter((ei) => ei.email !== conversation.email?.email),
            ]
        : /** if the mode is forward, don't have any "to" addresses to start */
          [];

  const getInitialDraft = (mode: EmailReplyType): EmailDraftState => ({
    status: "drafting",
    mode,
    draft: {
      recipientsInfo: Object.assign(
        {},
        ...Object.entries(
          mode === "Forward"
            ? { to: [], cc: [], bcc: [] }
            : {
                to: initialDraftToAssignment(mode),

                cc: filterOutSelfFromCcOrBcc(mode === "Reply All" ? cc : []),
                bcc: filterOutSelfFromCcOrBcc(mode === "Reply All" ? bcc : []),
              },
        ).map(([emailType, unvalidatedEmails]) => ({
          [emailType]: filterAndPreprocessEmailInfo(unvalidatedEmails),
        })),
        {
          inReplyTo: message?.email?.messageId,
          inReplyToMongoId: message?._id,
        },
      ),
      subject: `Re: ${subject}`,
      content: "",
    },
  });
  const initializeDraft = (): EmailDraftState => {
    const messageDraft = getPublicDraftReplyingToMessage(conversation, message);
    if (message.visibility === MessageVisibility.INTERNAL) {
      return { status: "noDraft" };
    } else if (messageDraft && !conversationJustMerged) {
      return {
        status: "drafting",
        mode:
          messageDraft.draftInfo?.emailDraftInfo?.replyType ||
          EmailReplyType.REPLY,
        draft: {
          recipientsInfo: {
            to: messageDraft.email?.to,
            cc: filterAndPreprocessEmailInfo(messageDraft.email?.cc || []),
            bcc: filterAndPreprocessEmailInfo(messageDraft.email?.bcc || []),
            inReplyTo: message?.email?.messageId,
            inReplyToMongoId: message?._id,
          },
          subject: `Re: ${conversation.subject}`,
          content: messageDraft.content,
        },
      };
    } else if (whichDraftModeToStartWith && !conversationJustMerged) {
      return getInitialDraft(whichDraftModeToStartWith);
    }
    return { status: "noDraft" };
  };

  const [replyDraft, setReplyDraft] =
    useState<EmailDraftState>(initializeDraft);

  const [triggerReinitalizeDraft, setTriggerReinitalizeDraft] = useState(false);

  useEffect(() => {
    if (triggerReinitalizeDraft) {
      setReplyDraft(initializeDraft());
      setTriggerReinitalizeDraft(false);
    } else if (conversationJustMerged) {
      setReplyDraft(initializeDraft());
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerReinitalizeDraft, conversationJustMerged]);

  /**
   * If the draft mode has changed, we need to reset the draft.
   * Otherwise, if it's been edited, update the stored draft.
   */
  const handleSetReplyDraft: Dispatch<SetStateAction<EmailDraftState>> = (
    newDraft: SetStateAction<EmailDraftState>,
  ) => {
    setReplyDraft((prevDraft: EmailDraftState) => {
      const updatedDraft =
        typeof newDraft === "function" ? newDraft(prevDraft) : newDraft;

      let updatedDraftOrSwitchedTypeDraft = updatedDraft;

      const shouldResetRecipientInfo =
        prevDraft.status === "drafting" &&
        updatedDraft.status === "drafting" &&
        updatedDraft.mode !== prevDraft.mode;
      if (shouldResetRecipientInfo) {
        updatedDraftOrSwitchedTypeDraft = getInitialDraft(updatedDraft.mode);
      }

      /**
       * We need to sync forwarded attachments if we're starting, switching to, or switching from forwarding.
       */
      if (updatedDraftOrSwitchedTypeDraft.status === "drafting") {
        const isNewDraftForwarding =
          updatedDraftOrSwitchedTypeDraft.mode === EmailReplyType.FORWARD;
        const isPreviousDraftForwarding =
          prevDraft.status === "drafting" &&
          prevDraft.mode === EmailReplyType.FORWARD;

        const shouldSyncForwardedFiles =
          isNewDraftForwarding !== isPreviousDraftForwarding;
        if (shouldSyncForwardedFiles) {
          updatedDraftOrSwitchedTypeDraft.draft.requiresAttachmentForwardSync = true;
        }
      }

      return updatedDraftOrSwitchedTypeDraft;
    });
  };

  const [hideReplies, setHideReplies] = useState(true);

  const messageHTML =
    messageContent.type === "messageHtml"
      ? messageContent.messageHtml
      : convertPlainTextToHtml(messageContent.messageText);

  let showToggleViewReplyButton = false;
  let shortenedMessage = messageHTML;
  if (hideReplies) {
    const { processedContent, showToggleViewReplyButton: showToggleViewReply } =
      cutOutRepliesFromMessageContent(shortenedMessage);
    showToggleViewReplyButton = showToggleViewReply;
    shortenedMessage = processedContent.replace(/<br\s*\/?>\s*$/, ""); // Remove trailing <br> tag if it exists
  } else {
    showToggleViewReplyButton = true;
  }

  type VaryingByMessageTypeProps = {
    avatarGroup: { headerColor: string; subtlerColor: string };
  };

  /* Most things are covered by css selectors of these classNames, but some things need to be passed as props to arbiter components. */
  const varyingByMessageTypePropsMap = {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    [emailMessageCss.internal]: {
      avatarGroup: {
        headerColor: "var(--redo-colors-text-text-primary-900) !important",
        subtlerColor: "var(--redo-colors-text-text-tertiary-600) !important",
      },
    },
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    [emailMessageCss.merchant]: {
      avatarGroup: {
        headerColor: "var(--redo-colors-text-text-primary-900) !important",
        subtlerColor: "var(--redo-colors-text-text-tertiary-600) !important",
      },
    },
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    [emailMessageCss.normal]: {
      avatarGroup: {
        headerColor: "var(--redo-colors-text-text-primary-900) !important",
        subtlerColor: "var(--redo-colors-text-text-primary-900) !important",
      },
    },
  };
  const messageVariantClassname =
    visibility === MessageVisibility.INTERNAL
      ? emailMessageCss.internal
      : isUser
        ? emailMessageCss.merchant
        : emailMessageCss.normal;

  const messageVariantProps = varyingByMessageTypePropsMap[
    messageVariantClassname as keyof typeof varyingByMessageTypePropsMap
  ] as VaryingByMessageTypeProps;

  /*
   Show & enable reply buttons if this message is public, we're not already replying, and we have an email message id
   Also hide reply buttons for chat messages before the chat converted to email message
   Having an email message id is super important so that drafts know what message they're replying to
   We could have probably just used the redo message id, which would have probably simplified some logic
   Also don't show if we are currently in the five second period between "send" click and actually sending
   This is important because a user could try to reply to the same message again (before we've actually sent it) which would override the draft message and break things
  */
  const hideReplyButtonsBecauseOfPlatformConversion =
    indexOfChatConversionMessage !== null &&
    messageIndex < indexOfChatConversionMessage;

  const shouldShowReplyButtons =
    visibility !== MessageVisibility.INTERNAL &&
    replyDraft.status === "noDraft" &&
    !hideReplyButtonsBecauseOfPlatformConversion &&
    canReply;

  // Disable the buttons if we are sending a message in this conversation already
  const shouldDisableReplyButtons = conversationSendStates?.has(
    conversation._id,
  );

  const commonButtonProps: Partial<RedoButtonProps> = {
    hierarchy: "secondary",
    size: "md",
    disabled: shouldDisableReplyButtons,
  };

  const replyButtons = (
    <Flex className={emailMessageCss.emailContentPaddedContainer} gap="xl">
      {shouldShowReplyButton && (
        <RedoButton
          IconLeading={CornerUpLeftSvg}
          onClick={() => {
            amplitude.logEvent("select-supportTicketEmailReply", {
              conversationId: conversation._id,
              channel: conversation.platform,
            });
            handleSetReplyDraft(getInitialDraft(EmailReplyType.REPLY));
          }}
          text="Reply"
          {...commonButtonProps}
        />
      )}

      {shouldShowReplyAllButton && (
        <RedoButton
          IconLeading={CornerUpLeftDoubleSvg}
          onClick={() => {
            amplitude.logEvent("select-supportTicketEmailReplyAll", {
              conversationId: conversation._id,
              channel: conversation.platform,
            });
            handleSetReplyDraft(getInitialDraft(EmailReplyType.REPLY_ALL));
          }}
          text="Reply All"
          {...commonButtonProps}
        />
      )}

      <RedoButton
        IconLeading={CornerUpRightSvg}
        onClick={() => {
          amplitude.logEvent("select-supportTicketEmailForward", {
            conversationId: conversation._id,
            channel: conversation.platform,
          });
          setShouldPopupRecipientsModalBecauseForwardButtonClicked(true);
          handleSetReplyDraft(getInitialDraft(EmailReplyType.FORWARD));
        }}
        text="Forward"
        {...commonButtonProps}
      />

      {message.user?._id === AI_AUTOPILOT_USER_ID && (
        <Flex>
          <AIFeedback
            conversationId={conversation._id}
            format="email"
            message={message}
          />
        </Flex>
      )}
    </Flex>
  );

  // Prepare info row content for expanded state
  const infoRowContent =
    visibility === MessageVisibility.PUBLIC ? (
      <>
        <CustomTooltip
          placement="bottom-end"
          title={
            <EmailAddressesTooltip
              bcc={bcc}
              cc={cc}
              date={date}
              from={from ?? { email: "", name: "" }}
              subject={subject}
              to={to}
            />
          }
        >
          <Flex
            align="center"
            className={classNames(
              emailMessageCss.emailInfoIcon,
              messageVariantClassname,
            )}
            justify="center"
            onMouseUp={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <InfoSvg />
          </Flex>
        </CustomTooltip>
        <Text
          color={messageVariantProps.avatarGroup.subtlerColor}
          fontSize="xs"
          pb="xxs"
          pt="xxs"
        >
          {stringifyEmailRecipientsInfo({ to, cc, bcc })}
        </Text>
      </>
    ) : null;

  // Prepare additional header content (badges and attachments info)
  const additionalHeaderContent = (
    <>
      {getPublicDraftReplyingToMessage(conversation, message) && (
        <RedoBadge color="gray" size="xs" text="Draft" />
      )}
      {visibility == MessageVisibility.INTERNAL &&
        (message.user?._id === AI_AUTOPILOT_USER_ID ? (
          <RedoBadge
            color="orange"
            segmentLeading={{ type: "icon", Icon: StarsIcon }}
            size="xs"
            text="AI handoff note"
          />
        ) : (
          <RedoBadge
            color="warning"
            size="xs"
            text="Internal note"
            type="alternate"
          />
        ))}
    </>
  );

  return (
    <EmailMessageContainer
      additionalHeaderContent={additionalHeaderContent}
      className={messageVariantClassname}
      conversation={conversation}
      headerRightContent={
        <Flex align="center" justify="flex-end" shrink="0">
          {!!files.length && (
            <RedoBadge
              segmentLeading={{ type: "icon", Icon: PaperclipSvg }}
              size="sm"
              text={`${files.length} attachment${files.length > 1 ? "s" : ""}`}
            />
          )}
          <Text
            color={messageVariantProps.avatarGroup.subtlerColor}
            fontSize="xs"
          >
            {date}
          </Text>
        </Flex>
      }
      id={id}
      infoRowContent={infoRowContent}
      message={message}
      profilePictures={profilePictures}
      showEmailContent={false} // We'll handle it manually in children
      startCollapsed={startCollapsed}
      teamName={team.name}
    >
      {/* Email content with custom message processing */}
      <Flex className={emailMessageCss.emailContentPaddedContainer}>
        <SimpleEmailMessage
          messageContent={{
            type: "messageHtml",
            messageHtml: shortenedMessage,
          }}
        />
      </Flex>

      {/* Attachments */}
      {!!files.length && (
        <Flex
          className={emailMessageCss.emailContentPaddedContainer}
          dir="row"
          gap="xl"
          wrap="wrap"
        >
          {files.map((file) => (
            <Flex
              className={emailMessageCss.attachment}
              key={`keyforemailattachments${file._id}`}
              radius="xs"
            >
              <AttachmentThumbnail
                description={file.description}
                mimeType={file.mimeType}
                url={file.url}
              />
            </Flex>
          ))}
        </Flex>
      )}

      {/* Toggle view reply button */}
      {showToggleViewReplyButton && (
        <Flex
          className={emailMessageCss.emailContentPaddedContainer}
          justify="flex-start"
        >
          <RedoButton
            hierarchy="tertiary"
            IconLeading={() => <DotsIconSvg />}
            onClick={() => setHideReplies((prev) => !prev)}
            size="sm"
          />
        </Flex>
      )}

      {/* Reply buttons */}
      {shouldShowReplyButtons && replyButtons}

      {/* Draft editor */}
      {replyDraft.status === "drafting" && canReply && (
        <Flex
          align="flex-start"
          className={emailMessageCss.messageInputContainer}
          justify="flex-start"
        >
          <Flex
            className={classNames(
              emailMessageCss.draftEditor,
              emailMessageCss.wrappingText,
            )}
            grow={1}
          >
            <MessageInput
              conversation={conversation}
              emailDraftProps={{
                draftInfo: replyDraft,
                handleSetReplyDraft,
                setTriggerReinitalizeDraft,
                shouldPopupRecipientsModalBecauseForwardButtonClicked,
                setShouldPopupRecipientsModalBecauseForwardButtonClicked,
              }}
              messageIdForKey={message._id}
              nextConversationInList={nextConversationInList}
              prevConversationInList={prevConversationInList}
              removeConversationFromProximity={removeConversationFromProximity}
              setActiveConversation={setActiveConversation}
              setErrorMessage={setErrorMessage}
              setShowErrorMessage={setShowErrorMessage}
              setShowFullCommentThread={setShowFullCommentThread}
              setTyping={setTyping}
              showFullCommentThread={showFullCommentThread}
              typing={typing}
            />
          </Flex>
        </Flex>
      )}
    </EmailMessageContainer>
  );
});
